import React from "react";
import { find, round, toUpper } from "lodash";
import { Center, Column, Row } from "@/shared/ui/Blocks";
import { Icon, IconName } from "@/shared/ui/Icon";
import { DimensionIcons } from "@/shared/constants";
import { SubtitleText } from "@/shared/ui/Typography";
import {
  DimensionWithScores,
  Extreme,
  PersonScore,
} from "@/features/people/hooks/useDimensionsWithScores";
import { DimensionContext } from "@/types/data";
import Chip from "@/shared/ui/Chip";
import { PeopleCompareScoreChart } from "@/shared/charts/PeopleCompareScoreChart";
import { Card } from "@/shared/ui/Card";
import { cn } from "@/shared/utils/shadcn";

export interface BaseDimensionDetailsProps {
  dimension: DimensionWithScores;
  scores: PersonScore[];
  extremes?: Extreme[];
  context?: DimensionContext;
  children?: React.ReactNode;
  defaultOpen?: boolean;
  DescriptionComponent?: React.ReactNode;
}

function BaseDimensionDetails(props: BaseDimensionDetailsProps) {
  const {
    dimension,
    scores,
    extremes,
    context = "user",
    DescriptionComponent,
    children,
  } = props;

  const { title } = dimension;

  const icon = DimensionIcons[
    dimension.id as keyof typeof DimensionIcons
  ] as IconName;

  const focusedObjectId =
    context === "user_comparison" ||
    context === "user_in_team" ||
    context === "user" ||
    context === "candidate"
      ? dimension?.score?.obj_id
      : null;

  return (
    <Column className="gap-4 w-full personality-map-dimension-single">
      <Column className="p-8 pb-[90px] rounded-lg bg-grey-100 print:bg-transparent print:p-0">
        <Row className="gap-4 pb-8 justify-between border-b border-grey-300 border-solid print:border-none print:pb-4">
          <Row className="items-start gap-2">
            <Column className="gap-4">
              <Row className="items-center gap-2">
                {icon && <Icon icon={icon} color="black" />}
                <h5>{title}</h5>
                <Center className="self-center rounded-full aspect-square h-7 bg-gray-900">
                  <span className="text-sm font-semibold text-white">
                    {round(dimension.score?.value || 0)}
                  </span>
                </Center>
              </Row>
              <SubtitleText
                className={cn("print:text-xs", icon && "ml-[32px]")}
              >
                {dimension.description || ""}
              </SubtitleText>
            </Column>
          </Row>
          {!!dimension?.alignment?.label && (
            <Chip color={dimension.alignment?.color} variant="solid" size="sm">
              <p className="text-white text-xs">
                {toUpper(dimension.alignment?.label || "")}
              </p>
            </Chip>
          )}
        </Row>
        {DescriptionComponent}
      </Column>
      <Card className="p-8 pt-4 mx-8 -mt-[68px] shadow-xl print:shadow-none print:p-4 overflow-visible print:mt-[0px] print:mx-0">
        <PeopleCompareScoreChart
          scores={scores}
          extremes={extremes || dimension.extremes}
          averageScore={dimension?.averageScore?.value || 0}
          showArea={["team", "user_in_team", "candidate"].includes(context)}
          areaColor={dimension.alignment?.color}
          minScore={find(dimension?.allScores, { key: "min" })?.value || 0}
          maxScore={find(dimension?.allScores, { key: "max" })?.value || 0}
          highlightedPersonId={focusedObjectId || undefined}
        />
      </Card>
      {children}
    </Column>
  );
}

export { BaseDimensionDetails };
